import LRUCache from 'lru-cache';

import buildCacheIndex from '@core/api/instance/utils/buildCacheIndex';
import parsePositiveNumber from '@core/api/instance/utils/parsePositiveNumber';
import parseBoolean from '@core/api/instance/utils/parseBoolean';

/**
 * Unique reference to the LRU instance used in this module
 */
let cacheInstance;

/**
 * Clears all cache from the LRUCache instance
 */
const destroyAdapterCache = () => {
  cacheInstance && cacheInstance.reset();
};

/**
 * Initializes the cache instance when configuring the adapter for the first time
 */
const initializeCache = () => {
  if (typeof cacheInstance === 'undefined') {
    cacheInstance = new LRUCache({
      max: 0,
    });
  }
};

/**
 * Extracts all parameters related to this adapter to be used locally and removes
 * them from the axios config.
 *
 * @param {Function} handler
 * @param {AxiosRequestConfig} rawConfig
 * @returns {[AdapterParams, AxiosRequestConfig, AxiosRequestConfig]}
 * @private
 */
const extractParams = handler => rawConfig => {
  const { throttle, forceUpdate, ...rest } = rawConfig;
  const params = {
    throttle: parsePositiveNumber(throttle),
    forceUpdate: parseBoolean(forceUpdate),
  };

  return handler([params, rest, rawConfig]);
};

/**
 * @param {LRUCache} cache
 * @param {AxiosAdapter} prevAdapter
 * @param {AxiosRequestConfig} config
 * @param {AdapterParams} adapterParams
 * @returns {AxiosAdapter}
 * @private
 */
const throttleAdapter = (cache, prevAdapter, config, adapterParams) => {
  const index = buildCacheIndex(config);
  const maxAge = adapterParams.throttle;
  const cachedResponse = cache.get(index);

  if (cachedResponse && !adapterParams.forceUpdate) {
    return cachedResponse;
  }

  const deferredResponse = prevAdapter(config)
    .then(resp => {
      cache.set(index, Promise.resolve(resp), maxAge);
      return resp;
    })
    .catch(err => {
      cache.del(index);
      return Promise.reject(err);
    });

  cache.set(index, deferredResponse, maxAge);
  return deferredResponse;
};

/**
 * @param {AxiosAdapter} prevAdapter
 * @returns {AxiosAdapter}
 */
const throttleAdapterEnhancer = prevAdapter => {
  initializeCache();

  return extractParams(([adapterParams, config]) =>
    config.method === 'get' && adapterParams.throttle > 0
      ? throttleAdapter(cacheInstance, prevAdapter, config, adapterParams)
      : prevAdapter(config),
  );
};

export default throttleAdapterEnhancer;
export { destroyAdapterCache };
